import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import type { KeptScan } from './scan-agent';
import { keptLine } from './scan-kept';
import './scan-kept.css';

export interface ScanKeptOfferProps {
  kept: KeptScan[];
  /** The kept scan whose pages are being brought back now. */
  restoring: string | null;
  /** A scan runs, or pages are on their way into the job. */
  busy: boolean;
  onAdd: (scan: KeptScan) => void;
  onDiscard: (scan: KeptScan) => void;
}

/** How many kept scans are offered at once; the next shows when one is added or discarded. */
const OFFERED = 3;

/**
 * Pages Paige Scan kept from a scan that never reached the job (its tab closed, reloaded or crashed): one plain line
 * each, "Add them" (they become this sheet's pages, as if just scanned) or "Discard".
 */
export function ScanKeptOffer(p: ScanKeptOfferProps): ReactNode {
  if (p.kept.length === 0) return null;
  return (
    <ul className="paige-scan-kept" aria-label="Pages not added">
      {p.kept.slice(0, OFFERED).map((scan) => (
        <li key={scan.scan}>
          <span>{keptLine(scan)}</span>
          {p.restoring === scan.scan ? <Restoring /> : <KeptActions {...p} scan={scan} />}
        </li>
      ))}
    </ul>
  );
}

function Restoring(): ReactNode {
  return (
    <span className="paige-scan-kept-restoring" role="status">
      <Loader2 size={13} className="paige-spin" aria-hidden="true" />
      Bringing them back…
    </span>
  );
}

function KeptActions(p: ScanKeptOfferProps & { scan: KeptScan }): ReactNode {
  const disabled = p.busy || p.restoring !== null;
  return (
    <span className="paige-scan-kept-actions">
      <button type="button" disabled={disabled} onClick={() => p.onAdd(p.scan)}>
        Add them
      </button>
      <button
        type="button"
        className="is-quiet"
        disabled={disabled}
        onClick={() => p.onDiscard(p.scan)}
      >
        Discard
      </button>
    </span>
  );
}
